"use client";

import { createContext, type ReactNode, useContext, useEffect, useState } from "react";

type FontScale = 1 | 1.1 | 1.2;

interface AccessibilityContextValue {
  fontScale: FontScale;
  highContrast: boolean;
  increaseFont: () => void;
  decreaseFont: () => void;
  canIncrease: boolean;
  canDecrease: boolean;
  toggleContrast: () => void;
}

const STORAGE_KEY = "gov-pe:a11y";

// Default no-op value lets components mount safely without the provider
// (e.g., isolated unit tests rendering Header without app shell)
const DEFAULT_VALUE: AccessibilityContextValue = {
  fontScale: 1,
  highContrast: false,
  increaseFont: () => {},
  decreaseFont: () => {},
  canIncrease: true,
  canDecrease: false,
  toggleContrast: () => {},
};

const AccessibilityContext = createContext<AccessibilityContextValue>(DEFAULT_VALUE);

/**
 * O contrato com o `<html>`: classe `high-contrast` e `font-size` na raiz.
 *
 * Os tokens de tamanho são `rem`, então mudar o `font-size` da raiz escala a
 * tipografia inteira do design system; as cores de alto contraste vivem num
 * bloco `html.high-contrast` no `system.css`. São só esses dois sinais — quem
 * quiser dirigir a acessibilidade por fora (um portal que já tem os próprios
 * botões A+/A− e o próprio armazenamento) precisa escrever exatamente isto, e
 * montar o provider com `managed={false}` para não disputar o mesmo `<html>`.
 */
export interface AccessibilityProviderProps {
  children: ReactNode;
  /**
   * `false` quando outra coisa é dona do `<html>` — tipicamente um
   * microfrontend dentro de um portal que já aplica contraste e escala. O
   * provider passa a **ler** o DOM em vez de escrevê-lo, e não persiste nada:
   * os componentes continuam sabendo o estado (para `aria-pressed`, para
   * trocar um logo), sem dois donos brigando pela mesma raiz.
   */
  managed?: boolean;
}

/** Lê do `<html>` o que o host aplicou. */
function readFromDom(): { fontScale: FontScale; highContrast: boolean } {
  const root = document.documentElement;
  const px = Number.parseFloat(root.style.fontSize || "16");
  const scale = Number.isFinite(px) ? px / 16 : 1;
  const fontScale: FontScale = scale >= 1.2 ? 1.2 : scale >= 1.1 ? 1.1 : 1;
  return { fontScale, highContrast: root.classList.contains("high-contrast") };
}

export function AccessibilityProvider({ children, managed = true }: AccessibilityProviderProps) {
  const [fontScale, setFontScale] = useState<FontScale>(1);
  const [highContrast, setHighContrast] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate from localStorage on mount
  useEffect(() => {
    if (!managed) {
      const fromDom = readFromDom();
      setFontScale(fromDom.fontScale);
      setHighContrast(fromDom.highContrast);
      setHydrated(true);
      return;
    }
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as { fontScale?: number; highContrast?: boolean };
        if (parsed.fontScale === 1 || parsed.fontScale === 1.1 || parsed.fontScale === 1.2) {
          setFontScale(parsed.fontScale);
        }
        if (typeof parsed.highContrast === "boolean") {
          setHighContrast(parsed.highContrast);
        }
      }
    } catch {
      // ignore malformed storage
    }
    setHydrated(true);
  }, [managed]);

  useEffect(() => {
    if (managed) return;
    const root = document.documentElement;
    const observer = new MutationObserver(() => {
      const fromDom = readFromDom();
      setFontScale(fromDom.fontScale);
      setHighContrast(fromDom.highContrast);
    });
    observer.observe(root, { attributes: true, attributeFilter: ["class", "style"] });
    return () => observer.disconnect();
  }, [managed]);

  // Apply changes to <html> and persist
  useEffect(() => {
    if (!hydrated || !managed) return;
    const root = document.documentElement;
    // `%` respeita o tamanho de fonte configurado no navegador; `px` o sobrescrevia.
    root.style.fontSize = `${Math.round(fontScale * 100)}%`;
    root.classList.toggle("high-contrast", highContrast);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ fontScale, highContrast }));
    } catch {
      // storage quota / private mode — ignore
    }
  }, [fontScale, highContrast, hydrated, managed]);

  /* Não gerenciado, os três viram no-op — e `canIncrease`/`canDecrease` saem
     `false`, então `AccessibilityControls` renderiza os botões desabilitados
     em vez de oferecer um controle que não faria nada. Quem tem a UI nesse
     caso é o host. */
  const increaseFont = () => managed && setFontScale((s) => (s === 1 ? 1.1 : s === 1.1 ? 1.2 : s));
  const decreaseFont = () => managed && setFontScale((s) => (s === 1.2 ? 1.1 : s === 1.1 ? 1 : s));
  const toggleContrast = () => managed && setHighContrast((v) => !v);

  return (
    <AccessibilityContext.Provider
      value={{
        fontScale,
        highContrast,
        increaseFont,
        decreaseFont,
        canIncrease: managed && fontScale < 1.2,
        canDecrease: managed && fontScale > 1,
        toggleContrast,
      }}
    >
      {children}
    </AccessibilityContext.Provider>
  );
}

export function useAccessibility() {
  return useContext(AccessibilityContext);
}
